import React, { useState, useEffect, useCallback } from 'react';
import {
  ContentCopy as CopyIcon,
  Link as LinkIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { TextField, Box, IconButton, Snackbar, Alert } from '@mui/material';
import { Button } from '@rentspree/component-2023.components.atoms.button';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';
import { Card } from '@rentspree/component-2023.components.organisms.card';

import { useScreeningSetup } from '../../hooks/useScreeningSetup';
import { applicationRepository } from '../../repositories/ApplicationRepository';
import { DecisionType, ApplicationResponse } from '../../types/application';
import { ApplicationColumn } from '../molecules/ApplicationColumn';
import { ExpertHandleCard } from '../molecules/ExpertHandleCard';

export interface ApplicationsTabProps {
  listingId: string;
  onStartScreening?: () => void;
  onFindAgent?: () => void;
  onCreateLease?: (applicationId: string) => void;
  className?: string;
  style?: React.CSSProperties;
}

export const ApplicationsTab: React.FC<ApplicationsTabProps> = ({
  listingId,
  onStartScreening,
  onFindAgent,
  onCreateLease,
  className,
  style,
}) => {
  const [emails, setEmails] = useState('');
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [applicationData, setApplicationData] =
    useState<ApplicationResponse | null>(null);
  const [isLoadingApplications, setIsLoadingApplications] = useState(false);

  const { screeningSetup, isLoading, hasScreeningSetup } =
    useScreeningSetup(listingId);
  const applicationLink =
    screeningSetup?.applicationLink || 'https://apply.link/23xyz';

  const loadApplications = useCallback(async () => {
    setIsLoadingApplications(true);
    try {
      const filters = {
        listingId,
      };
      const response = await applicationRepository.getApplications(filters);
      setApplicationData(response);
    } catch (error) {
      console.error('Failed to load applications:', error);
    } finally {
      setIsLoadingApplications(false);
    }
  }, [listingId]);

  // Load applications when component mounts or when hasScreeningSetup changes
  useEffect(() => {
    if (hasScreeningSetup) {
      loadApplications();
    }
  }, [hasScreeningSetup, loadApplications]);

  const handleStartScreening = () => {
    onStartScreening?.();
  };

  const handleFindAgent = () => {
    onFindAgent?.();
  };

  const handleSendEmails = () => {
    // TODO: Implement email sending logic
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(applicationLink);
    setShowCopySuccess(true);
  };

  const handleShareLink = () => {
    // TODO: Implement link sharing logic
  };

  const handlePrintLink = () => {
    // TODO: Implement print functionality
  };

  const handleMakeDecision = async (
    applicationId: string,
    decision: DecisionType
  ) => {
    try {
      await applicationRepository.makeDecision(applicationId, decision);
      loadApplications(); // Reload to get updated data
    } catch (error) {
      console.error('Failed to make decision:', error);
    }
  };

  const handleCreateLease = (applicationId: string) => {
    onCreateLease?.(applicationId);
  };

  const filteredApplications = applicationData?.applications || [];
  const hasApplications = applicationData && applicationData.counts.total > 0;
  const showApplicationsList =
    hasScreeningSetup && filteredApplications.length > 0;

  if (isLoading) {
    return (
      <Box
        className={className}
        style={style}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: { xs: 3, lg: 4 },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Card
            style={{
              padding: '32px',
              marginBottom: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
            }}
          >
            <Typography
              variant='body1'
              style={{
                color: '#757575',
                fontSize: '16px',
              }}
            >
              Loading screening setup...
            </Typography>
          </Card>
        </Box>
        <Box
          sx={{
            width: { xs: '100%', lg: 358 },
            flexShrink: 0,
          }}
        >
          <ExpertHandleCard onFindAgent={handleFindAgent} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className={className}
      style={style}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: hasApplications ? 'column' : 'row' },
        gap: { xs: 3, lg: 4 },
        width: '100%',
      }}
    >
      <Box sx={{ flex: 1, width: '100%' }}>
        {hasScreeningSetup ? (
          <>
            {/* Applications Section */}
            <Card
              style={{
                padding: '32px',
                marginBottom: 24,
                width: '100%',
              }}
            >
              <div style={{ marginBottom: 32 }}>
                <Typography
                  variant='h5'
                  style={{
                    color: '#222222',
                    fontWeight: 700,
                    marginBottom: 16,
                    fontSize: '24px',
                    lineHeight: '32px',
                  }}
                >
                  Applications
                </Typography>
                <Typography
                  variant='body1'
                  style={{
                    color: '#757575',
                    fontSize: '18px',
                    lineHeight: '28px',
                    marginBottom: 0,
                  }}
                >
                  Review and manage applicant submissions for your listing.
                </Typography>
              </div>

              {isLoadingApplications ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 200,
                  }}
                >
                  <Typography
                    variant='body1'
                    style={{
                      color: '#757575',
                      fontSize: '16px',
                    }}
                  >
                    Loading applications...
                  </Typography>
                </Box>
              ) : showApplicationsList ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                    gap: { xs: 2, md: 4 },
                    marginTop: 2,
                    alignItems: 'start',
                    width: '100%',
                    maxWidth: 'none',
                  }}
                >
                  <ApplicationColumn
                    title='New'
                    applications={
                      applicationData?.applications.filter(
                        app => app.status === 'new'
                      ) || []
                    }
                    onMakeDecision={handleMakeDecision}
                    onCreateLease={handleCreateLease}
                  />
                  <ApplicationColumn
                    title='In Review'
                    applications={
                      applicationData?.applications.filter(
                        app => app.status === 'in_review'
                      ) || []
                    }
                    onMakeDecision={handleMakeDecision}
                    onCreateLease={handleCreateLease}
                  />
                  <ApplicationColumn
                    title='Decision Made'
                    applications={
                      applicationData?.applications.filter(
                        app => app.status === 'decision_made'
                      ) || []
                    }
                    onMakeDecision={handleMakeDecision}
                    onCreateLease={handleCreateLease}
                  />
                </Box>
              ) : (
                <Box sx={{ marginTop: 2, textAlign: 'center', padding: 4 }}>
                  <Typography
                    variant='h6'
                    sx={{ color: '#757575', marginBottom: 2 }}
                  >
                    No applications yet
                  </Typography>
                  <Typography variant='body2' sx={{ color: '#9e9e9e' }}>
                    Share your application link to start receiving applications
                    from interested renters.
                  </Typography>
                </Box>
              )}
            </Card>

            {/* Link Sharing Section - Only show when there are no applications */}
            {!hasApplications && (
              <Card
                style={{
                  padding: '32px',
                  marginBottom: 24,
                }}
              >
                <div style={{ marginBottom: 32 }}>
                  <Typography
                    variant='h5'
                    style={{
                      color: '#222222',
                      fontWeight: 700,
                      marginBottom: 16,
                      fontSize: '24px',
                      lineHeight: '32px',
                    }}
                  >
                    Share your secure application link
                  </Typography>
                  <Typography
                    variant='body1'
                    style={{
                      color: '#757575',
                      fontSize: '18px',
                      lineHeight: '28px',
                      marginBottom: 0,
                    }}
                  >
                    Send this link to applicants. When they apply, we'll run the
                    reports you selected.
                  </Typography>
                </div>

                <div style={{ marginBottom: 32 }}>
                  <Typography
                    variant='h6'
                    style={{
                      color: '#222222',
                      fontWeight: 600,
                      marginBottom: 12,
                      fontSize: '18px',
                      lineHeight: '28px',
                    }}
                  >
                    Send to applicant by email
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <TextField
                      fullWidth
                      placeholder='Enter one or more emails, separated by commas'
                      value={emails}
                      onChange={e => setEmails(e.target.value)}
                      variant='outlined'
                      sx={{
                        flex: 1,
                        minWidth: 250,
                        '& .MuiOutlinedInput-root': {
                          height: 50,
                          borderRadius: '8px',
                          '& fieldset': {
                            borderColor: '#bdbdbd',
                          },
                          '&:hover fieldset': {
                            borderColor: '#bdbdbd',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1f15e1',
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          fontSize: '16px',
                          color: '#222222',
                          '&::placeholder': {
                            color: '#adaebc',
                            opacity: 1,
                          },
                        },
                      }}
                    />
                    <Button
                      variant='contained'
                      color='primary'
                      size='large'
                      onClick={handleSendEmails}
                      style={{
                        backgroundColor: '#1f15e1',
                        color: '#ffffff',
                        fontWeight: 600,
                        fontSize: '16px',
                        padding: '13px 32px',
                        borderRadius: 8,
                        textTransform: 'none',
                        height: 50,
                        minWidth: 88,
                      }}
                    >
                      Send
                    </Button>
                  </Box>
                </div>

                <div>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <TextField
                      value={applicationLink}
                      variant='outlined'
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        flex: 1,
                        minWidth: 250,
                        '& .MuiOutlinedInput-root': {
                          height: 50,
                          borderRadius: '8px',
                          backgroundColor: '#f9f9ff',
                          '& fieldset': {
                            borderColor: '#bdbdbd',
                          },
                          '&:hover fieldset': {
                            borderColor: '#bdbdbd',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#bdbdbd',
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          fontSize: '16px',
                          color: '#222222',
                        },
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        onClick={handleCopyLink}
                        sx={{
                          width: 48,
                          height: 48,
                          border: '1px solid #bdbdbd',
                          borderRadius: '8px',
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                      >
                        <CopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        onClick={handleShareLink}
                        sx={{
                          width: 48,
                          height: 48,
                          border: '1px solid #bdbdbd',
                          borderRadius: '8px',
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                      >
                        <LinkIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        onClick={handlePrintLink}
                        sx={{
                          width: 48,
                          height: 48,
                          border: '1px solid #bdbdbd',
                          borderRadius: '8px',
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                      >
                        <PrintIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </div>
              </Card>
            )}
          </>
        ) : (
          <Card
            style={{
              padding: '32px',
              marginBottom: 24,
            }}
          >
            <div style={{ marginBottom: 32 }}>
              <Typography
                variant='h5'
                style={{
                  color: '#222222',
                  fontWeight: 700,
                  marginBottom: 16,
                  fontSize: '24px',
                  lineHeight: '32px',
                }}
              >
                Get ready to screen applicants
              </Typography>
              <Typography
                variant='body1'
                style={{
                  color: '#757575',
                  fontSize: '18px',
                  lineHeight: '28px',
                  marginBottom: 0,
                }}
              >
                First, we'll help you create a secure application link. Share it
                with interested renters to receive applications and screening
                reports.
              </Typography>
            </div>
            <div>
              <Button
                variant='contained'
                color='primary'
                size='large'
                onClick={handleStartScreening}
                style={{
                  backgroundColor: '#1f15e1',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '18px',
                  padding: '17px 32px',
                  borderRadius: 12,
                  textTransform: 'none',
                  height: 60,
                  minWidth: 200,
                }}
              >
                Start Screening
              </Button>
            </div>
          </Card>
        )}
      </Box>

      {/* Sidebar - Only show when there are no applications */}
      {!hasApplications && (
        <Box
          sx={{
            width: { xs: '100%', lg: 358 },
            flexShrink: 0,
          }}
        >
          <ExpertHandleCard onFindAgent={handleFindAgent} />
        </Box>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={showCopySuccess}
        autoHideDuration={3000}
        onClose={() => setShowCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowCopySuccess(false)}
          severity='success'
          sx={{ width: '100%' }}
        >
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};
